import { memo, useState } from "react";

import { motion, AnimatePresence, Variants } from "framer-motion";

/**
 * The Carousel Slide Interface.
 * @interface
 * @name CarouselSlide
 * @kind interface
 */
interface CarouselSlide {
  /**
   * The URL for the slide.
   * @property
   * @name url
   * @kind property
   * @memberof CarouselSlide
   * @type {string}
   */
  url: string;
  /**
   * The title for the slide.
   * @property
   * @name title
   * @kind property
   * @memberof CarouselSlide
   * @type {string}
   */
  title: string;
  /**
   * The description for the slide.
   * @property
   * @name description
   * @kind property
   * @memberof CarouselSlide
   * @type {string}
   */
  description: string;
}

/**
 * Props for Carousel Component.
 * @interface
 * @name CarouselComponentProps
 * @kind interface
 * @exports
 */
export interface CarouselComponentProps {
  /**
   * The carousel slides.
   * @property
   * @name slides
   * @kind property
   * @memberof CarouselComponentProps
   * @type {CarouselSlide[]}
   */
  slides: CarouselSlide[];
}

/**
 * Variants allow you to define animation
 * states and organise them by name.
 * @constant
 * @name variants
 * @kind variable
 * @type {CarouselVariants}
 */
const variants: Variants = {
  initial: (direction: number) => {
    return {
      x: direction > 0 ? 200 : -200,
      opacity: 0,
    };
  },
  animate: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      x: direction > 0 ? -200 : 200,
      opacity: 0,
    };
  },
};

/**
 * Memoized Carousel Component.
 * @constant
 * @name CarouselComponent
 * @kind variable
 * @type {React.NamedExoticComponent<CarouselComponentProps>}
 */
const CarouselComponent: React.NamedExoticComponent<CarouselComponentProps> =
  memo(
    /**
     * Carousel Component.
     * @function
     * @name CarouselComponent
     * @kind function
     * @memberof CarouselComponent
     * @param {CarouselComponentProps} props Props for Carousel Component.
     * @returns {JSX.Element} Carousel Component.
     */
    function CarouselComponent(props: CarouselComponentProps): JSX.Element {
      const [currentIndex, setCurrentIndex] = useState(0);
      const [direction, setDirection] = useState(0);

      function selectStep(index: number) {
        if (index > currentIndex) {
          setDirection(1);
        } else {
          setDirection(-1);
        }
        setCurrentIndex(index);
      }

      return (
        <div className="flex h-max flex-col justify-start">
          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              custom={direction}
              transition={{ duration: 0.2 }}
              className="mb-6 flex h-full flex-col justify-center gap-y-5"
            >
              <div className="flex justify-center">
                <img
                  style={{ height: 200, width: 320 }}
                  className="object-contain"
                  src={props.slides[currentIndex].url}
                  alt="slide"
                />
              </div>
              <div className="flex justify-center">
                <h1 className="text-center text-3xl text-white">
                  {props.slides[currentIndex].title}
                </h1>
              </div>
              <div className="flex justify-center break-words">
                <h4 className="text-1xl w-3/5 break-words px-5 text-center text-white">
                  {props.slides[currentIndex].description}
                </h4>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center gap-x-3 pb-10">
            {props.slides.map((slide, slideIndex) => (
              <div
                className={`cursor-pointer text-6xl text-white  duration-1000 ${
                  slideIndex === currentIndex ? "opacity-100" : "opacity-50"
                }`}
                key={slideIndex}
                onClick={() => selectStep(slideIndex)}
              >
                &bull;
              </div>
            ))}
          </div>
        </div>
      );
    }
  );

export default CarouselComponent;
